import {
	BaseDataItem,
	Component,
	MediaContainerWithDividersDesign,
	ResolvedRefArray,
	ResolvedVariantRelation,
	Style,
} from '@wix/thunderbolt-becky-types'
import { CompNode, createCompNode, envRefs, NodeRefs } from '@wix/thunderbolt-catharsis'
import { Video } from '@wix/thunderbolt-components-native'
import {
	LoginSocialBarDefinition,
	MediaControlsDefinition,
	QuickActionBarItemDefinition,
	TinyMenuDefinition,
	VideoBoxControlsDefinition,
	VideoBoxDefinition,
	WPhotoDefinition,
	ImageXDefinition,
	LoginSocialBarData,
} from '@wix/thunderbolt-components'
import { isVariantRelation } from './cursorShapeIdsNode'

const createShapesCompNode = <TDeps>(obj: CompNode<TDeps, Array<string>, {}>) => createCompNode(obj)

const PopupCloseIconButton = createShapesCompNode({
	getDependencies: (component: Component, refs: NodeRefs<{}>) => {
		return component.styleId ? { style: refs.styleId(component.styleId) } : {}
	},
	toViewItem: ({ skin }, { style }) => {
		return [(style as Style)?.skin ?? skin!]
	},
})

const ClassicSection = createShapesCompNode({
	getDependencies: (component: Component, refs: NodeRefs<{}>) =>
		refs.designQuery<MediaContainerWithDividersDesign | ResolvedRefArray<MediaContainerWithDividersDesign>>(
			component.designQuery!
		),
	toViewItem: (__, compDesign) => {
		if (isRefArray<MediaContainerWithDividersDesign>(compDesign)) {
			return compDesign.values
				.map(
					(variant) => (isVariantRelation(variant) ? variant.to : variant) as MediaContainerWithDividersDesign
				)
				.flatMap(getMediaContainerWithDividersShapes)
		}
		return getMediaContainerWithDividersShapes(compDesign)
	},
})

const MediaPlayer = createShapesCompNode({
	getDependencies: (component: Component, refs: NodeRefs<{}>) => ({
		compProps: refs.propertyQuery<VideoBoxDefinition['property']>(component.propertyQuery!),
		compDesign: refs.designQuery<VideoBoxDefinition['design']>(component.designQuery!),
	}),
	toViewItem: (__, { compProps, compDesign }) => {
		const background = compDesign?.background?.mediaRef as Video | undefined
		const overrideMask = compProps?.overrideMask

		const mask = overrideMask || background?.mask
		return mask?.svgId ? [mask.svgId] : []
	},
})

const MediaOverlayControls = createShapesCompNode({
	getDependencies: (component: Component, refs: NodeRefs<{}>) =>
		refs.designQuery<VideoBoxControlsDefinition['design']>(component.designQuery!),
	toViewItem: (__, deps) => {
		if (!deps || !deps.icons) {
			return []
		}
		const playButton = deps.icons.find((icon) => icon.name === 'playButton')
		return typeof playButton?.svgId !== 'undefined' ? [playButton.svgId] : []
	},
})

export const isRefArray = <T>(item: BaseDataItem): item is ResolvedRefArray<T> => item?.type === 'RefArray'

const getMediaContainerWithDividersShapes = (designItem: MediaContainerWithDividersDesign) =>
	[designItem?.dividerTop?.svgId, designItem?.dividerBottom?.svgId].filter((svgId) => svgId && svgId !== 'none')

const StripColumnsContainer = createShapesCompNode({
	getDependencies: (component: Component, refs: NodeRefs<{}>) =>
		refs.designQuery<MediaContainerWithDividersDesign | ResolvedRefArray<MediaContainerWithDividersDesign>>(
			component.designQuery!
		),
	toViewItem: (__, compDesign) => {
		if (isRefArray<MediaContainerWithDividersDesign>(compDesign)) {
			const [designItem, ...variants] = compDesign.values as [
				MediaContainerWithDividersDesign,
				...Array<ResolvedVariantRelation<MediaContainerWithDividersDesign>>
			]

			return [designItem, ...variants.map((x) => x.to)].flatMap(getMediaContainerWithDividersShapes)
		}
		return getMediaContainerWithDividersShapes(compDesign)
	},
})

const QuickActionBarItem = createShapesCompNode({
	getDependencies: (component: Component, refs: NodeRefs<{}>) =>
		refs.dataQuery<QuickActionBarItemDefinition['data']>(component.dataQuery!),
	toViewItem: (__, compData) => (compData ? [`svgshape.v2.Svg_${compData.icon}`] : []),
})

const WPhoto = createShapesCompNode({
	getDependencies: (component: Component, refs: NodeRefs<{}>) => ({
		compProps: refs.propertyQuery<WPhotoDefinition['property']>(component.propertyQuery!),
		compData: refs.dataQuery<WPhotoDefinition['data']>(component.dataQuery!),
	}),
	toViewItem: (__, { compProps, compData }) => {
		const svgId = compProps?.overrideCrop?.svgId || compData?.crop?.svgId
		return typeof svgId !== 'undefined' ? [svgId] : []
	},
})

const ImageX = createShapesCompNode({
	getDependencies: (component: Component, refs: NodeRefs<{}>) => ({
		compData: refs.dataQuery<ImageXDefinition['data']>(component.dataQuery!),
	}),
	toViewItem: (__, { compData }) => {
		const svgId = compData?.mask?.svgId
		return typeof svgId !== 'undefined' ? [svgId] : []
	},
})

function findLoginSocialBarsShapes(loginSocialBarRef: LoginSocialBarData | undefined, isMobileView: boolean) {
	const items = loginSocialBarRef?.iconItemsRef?.menuRef?.items ?? []
	return items
		.filter((item) => (isMobileView ? item.isVisibleMobile : item.isVisible))
		.map((item) => item.iconRef?.svgId)
		.filter((item) => item)
}

const TinyMenu = createShapesCompNode({
	getDependencies: (component: Component, refs: NodeRefs<{}>) => ({
		compData: refs.dataQuery<TinyMenuDefinition['data']>(component.dataQuery!),
		isMobileView: envRefs.isMobileView,
	}),
	toViewItem: (__, { compData, isMobileView }) => findLoginSocialBarsShapes(compData.loginSocialBarRef, isMobileView),
})

const MediaControls = createShapesCompNode({
	getDependencies: (component: Component, refs: NodeRefs<{}>) =>
		refs.designQuery<MediaControlsDefinition['design']>(component.designQuery!),
	toViewItem: (__, deps) =>
		(deps?.icons || [])
			.filter((icon) => icon.name === 'volumeOn' || icon.name === 'volumeOff' || icon.name === 'noAudio')
			.map((icon) => icon.svgId),
})

const LoginSocialBar = createShapesCompNode({
	getDependencies: (component: Component, refs: NodeRefs<{}>) => ({
		compData: refs.dataQuery<LoginSocialBarDefinition['data']>(component.dataQuery!),
		isMobileView: envRefs.isMobileView,
	}),
	toViewItem: (__, { compData, isMobileView }) => findLoginSocialBarsShapes(compData, isMobileView),
})

const simpleCompNode = createShapesCompNode({
	getDependencies: (component: Component, refs: NodeRefs<{}>) =>
		refs.dataQuery<{ svgId: string }>(component.dataQuery!),
	toViewItem: (__, compData) => [compData?.svgId],
})

const simpleComps = [
	'BackToTopButton',
	'Breadcrumbs',
	'RatingsInput',
	'RatingsDisplay',
	'StylableButton',
	'HamburgerOpenButton',
	'HamburgerCloseButton',
	'SlideshowButton',
	'VectorImage',
]

const components: Record<string, CompNode<any, Array<string>, {}>> = {
	...simpleComps.reduce((acc, compName) => Object.assign(acc, { [compName]: simpleCompNode }), {}),
	ClassicSection,
	MediaControls,
	MediaOverlayControls,
	MediaPlayer,
	PopupCloseIconButton,
	QuickActionBarItem,
	Section: ClassicSection,
	StripColumnsContainer,
	TinyMenu,
	LoginSocialBar,
	WPhoto,
	ImageX,
}

export const isValidShape = (shapeId: string | undefined): shapeId is string =>
	!!(typeof shapeId === 'string' && (/^svgshape\.v[12]/.test(shapeId) || shapeId.endsWith('.svg')))

export const shapeIdsNode = createCompNode({
	getDependencies: (component: Component, refs: NodeRefs<{}>) => {
		const compNode = components[component.componentType]
		return compNode?.getDependencies(component, refs)
	},
	toViewItem: (component, deps) => {
		const compNode = components[component.componentType]
		const shapes = compNode?.toViewItem(component, deps)
		return shapes?.filter(isValidShape)
	},
})
